import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { z } from 'zod';

// 型定義
export interface Clinic {
  _id: string;
  name: string;
  prefecture: string;
  address: string;
  phone: string;
}

export interface Slot {
  _id: string;
  clinicId: string;
  date: string;
  time: string;
  available: boolean;
}

export interface PersonalInfo {
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  phone: string;
  email: string;
  request: string;
}

export interface ReservationState {
  prefecture: string;
  clinic: Clinic | null;
  dateTime: { date: string; time: string } | null;
  personalInfo: PersonalInfo;
  reservationNumber: string;
  loading: boolean;
  error: string | null;
}

// アクション型定義
type ReservationAction =
  | { type: 'SET_PREFECTURE'; payload: string }
  | { type: 'SET_CLINIC'; payload: Clinic }
  | { type: 'SET_DATE_TIME'; payload: { date: string; time: string } }
  | { type: 'SET_PERSONAL_INFO'; payload: Partial<PersonalInfo> }
  | { type: 'SET_RESERVATION_NUMBER'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET_FORM' };

// 初期状態
const initialState: ReservationState = {
  prefecture: '',
  clinic: null,
  dateTime: null,
  personalInfo: {
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    phone: '',
    email: '',
    request: '',
  },
  reservationNumber: '',
  loading: false,
  error: null,
};

// リデューサー
const reservationReducer = (state: ReservationState, action: ReservationAction): ReservationState => {
  switch (action.type) {
    case 'SET_PREFECTURE':
      return {
        ...state,
        prefecture: action.payload,
        clinic: null,
        dateTime: null,
      };
    case 'SET_CLINIC':
      return {
        ...state,
        clinic: action.payload,
        dateTime: null,
      };
    case 'SET_DATE_TIME':
      return {
        ...state,
        dateTime: action.payload,
      };
    case 'SET_PERSONAL_INFO':
      return {
        ...state,
        personalInfo: {
          ...state.personalInfo,
          ...action.payload,
        },
      };
    case 'SET_RESERVATION_NUMBER':
      return {
        ...state,
        reservationNumber: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
};

// コンテキスト作成
interface ReservationContextType extends ReservationState {
  setPrefecture: (prefecture: string) => void;
  setClinic: (clinic: Clinic) => void;
  setDateTime: (dateTime: { date: string; time: string }) => void;
  setPersonalInfo: (info: Partial<PersonalInfo>) => void;
  setReservationNumber: (number: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  resetForm: () => void;
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined);

// プロバイダーコンポーネント
interface ReservationProviderProps {
  children: ReactNode;
}

export const ReservationProvider: React.FC<ReservationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reservationReducer, initialState);

  // アクション
  const setPrefecture = (prefecture: string) => {
    dispatch({ type: 'SET_PREFECTURE', payload: prefecture });
  };

  const setClinic = (clinic: Clinic) => {
    dispatch({ type: 'SET_CLINIC', payload: clinic });
  };

  const setDateTime = (dateTime: { date: string; time: string }) => {
    dispatch({ type: 'SET_DATE_TIME', payload: dateTime });
  };

  const setPersonalInfo = (info: Partial<PersonalInfo>) => {
    dispatch({ type: 'SET_PERSONAL_INFO', payload: info });
  };

  const setReservationNumber = (number: string) => {
    dispatch({ type: 'SET_RESERVATION_NUMBER', payload: number });
  };

  const setLoading = (isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  return (
    <ReservationContext.Provider
      value={{
        ...state,
        setPrefecture,
        setClinic,
        setDateTime,
        setPersonalInfo,
        setReservationNumber,
        setLoading,
        setError,
        resetForm,
      }}
    >
      {children}
    </ReservationContext.Provider>
  );
};

// カスタムフック
export const useReservation = (): ReservationContextType => {
  const context = useContext(ReservationContext);
  if (!context) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
};
